import React, { useEffect, useRef, useState } from 'react';
import { Check, Heart } from 'lucide-react';
import { getFamilyInterests, signupForFamilyList } from '@/api/familyList';
import './FamilySignupPage.css';
import { consumeFamilyPrefill } from '@/components/Expo/familyPrefill';

export const FAMILY_CONSENT_TEXT = "Send me news from Let The Beat Build about programs, events and offers. I'm 18 or older.";

function readEventAndStripUrl() {
  const url = new URL(window.location.href);
  const event = url.searchParams.get('event') || '';
  if (url.searchParams.has('event')) {
    url.searchParams.delete('event');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }
  return event;
}

export function FamilySignupPage() {
  const eventRef = useRef(null);
  if (eventRef.current === null) eventRef.current = readEventAndStripUrl();
  const prefill = useRef(null);
  if (prefill.current === null) prefill.current = consumeFamilyPrefill(eventRef.current);
  const [firstName, setFirstName] = useState(prefill.current.firstName || '');
  const [email, setEmail] = useState(prefill.current.email || '');
  const [website, setWebsite] = useState('');
  const [interests, setInterests] = useState([]);
  const [selected, setSelected] = useState([]);
  const [consent, setConsent] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    const existing = document.head.querySelector('meta[name="referrer"]');
    const previous = existing?.getAttribute('content');
    const meta = existing || document.createElement('meta');
    meta.setAttribute('name', 'referrer');
    meta.setAttribute('content', 'no-referrer');
    if (!existing) document.head.appendChild(meta);

    const bodyOverflow = document.body.style.overflow;
    const root = document.getElementById('root');
    const rootOverflow = root?.style.overflow;
    document.body.style.overflow = 'auto';
    if (root) root.style.overflow = 'auto';

    let active = true;
    getFamilyInterests()
      .then((items) => { if (active) setInterests(Array.isArray(items) ? items : []); })
      .catch((requestError) => { if (active) setError(requestError.message); });
    return () => {
      active = false;
      if (existing) {
        if (previous === null) existing.removeAttribute('content');
        else existing.setAttribute('content', previous);
      } else meta.remove();
      document.body.style.overflow = bodyOverflow;
      if (root) root.style.overflow = rootOverflow;
    };
  }, []);

  const toggleInterest = (id) => {
    setSelected((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };

  const reset = () => {
    setFirstName(''); setEmail(''); setWebsite(''); setSelected([]); setConsent(false); setError(''); setComplete(false);
  };

  const submit = async (submitEvent) => {
    submitEvent.preventDefault();
    if (!consent || pending) return;
    setPending(true); setError('');
    const payload = { email, first_name: firstName, interests: selected, consent: true, website };
    if (eventRef.current) payload.event = eventRef.current;
    try {
      await signupForFamilyList(payload);
      setComplete(true);
    } catch (requestError) {
      setError(requestError.message || 'Something went wrong. Please try again.');
    } finally {
      setPending(false);
    }
  };

  return <main className="family-page" data-testid="family-signup-page">
    <div className="family-glow family-glow-one" />
    <div className="family-glow family-glow-two" />
    <section className="family-card">
      <div className="family-pizzul-wrap"><img src="/pizzul/happy.png" alt="Happy Pizzul" /></div>
      {complete ? <div className="family-success">
        <span className="family-success-icon" aria-hidden="true"><Check size={30} /></span>
        <p className="family-eyebrow">Welcome to the family</p>
        <h1>You're on the list.</h1>
        <p>Every email has an unsubscribe link.</p>
        <button type="button" onClick={reset}>Sign up someone else</button>
      </div> : <>
        <header className="family-header">
          <p className="family-eyebrow"><Heart size={15} aria-hidden="true" /> Let The Beat Build</p>
          <h1>Join the LTBB family list</h1>
          <p>Programs, events and offers for families and adults. Pick what you're into.</p>
        </header>
        <form className="family-form" onSubmit={submit}>
          <div className="family-name-email">
            <label htmlFor="family-first-name">First name <span>(optional)</span></label>
            <input id="family-first-name" autoComplete="given-name" maxLength={60} value={firstName} onChange={(inputEvent) => setFirstName(inputEvent.target.value)} />
            <label htmlFor="family-email">Email address</label>
            <input id="family-email" type="email" autoComplete="email" required value={email} onChange={(inputEvent) => setEmail(inputEvent.target.value)} />
          </div>

          <fieldset className="family-interests">
            <legend>Pick any <span>(optional)</span></legend>
            <div className="family-interest-grid">
              {interests.map((interest) => {
                const checked = selected.includes(interest.id);
                return <label key={interest.id} className={checked ? 'is-selected' : ''}>
                  <input type="checkbox" checked={checked} onChange={() => toggleInterest(interest.id)} />
                  <span>{interest.label}</span>
                </label>;
              })}
            </div>
          </fieldset>

          <div className="family-honeypot" aria-hidden="true">
            <label htmlFor="family-website">Website</label>
            <input id="family-website" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={website} onChange={(inputEvent) => setWebsite(inputEvent.target.value)} />
          </div>

          <label className="family-consent">
            <input type="checkbox" checked={consent} onChange={(inputEvent) => setConsent(inputEvent.target.checked)} />
            <span>{FAMILY_CONSENT_TEXT}</span>
          </label>
          {error && <p className="family-alert" role="alert">{error}</p>}
          <button className="family-submit" type="submit" disabled={!consent || pending}>{pending ? 'Signing you up...' : 'Sign me up'}</button>
        </form>
      </>}
    </section>
    <p className="family-footer"><a href="https://letthebeatbuild.me">letthebeatbuild.me</a></p>
  </main>;
}

export default FamilySignupPage;
